import { v4 as uuidv4 } from "uuid";
import { escapeXmlText, getPDFBase64 } from "./generateW8BENXML";
import { normalizeDateToSlash } from "../../../Utility/utility";
import { parseAddressComponents } from "../../../utils/formUtils";
import { getStateCode } from "../../../utils/formUtils";

// Document type mapping with TypeID and Type
const documentTypeMapping: Record<string, { typeId: string; type: string }> = {
  partnership_agreement: {
    typeId: "74B29767-B166-49A1-90C6-04D919FFF021",
    type: "Partnership Agreement",
  },
  trust_deed: {
    typeId: "E0E34437-CC13-487C-863E-248204E61086",
    type: "Trust Deed or Certified Extracts",
  },
  certificate_registration: {
    typeId: "F0B2D5EC-5C24-4FE5-A571-2610610FA3D1",
    type: "Certificate of Registration",
  },
  certificate_incorporation: {
    typeId: "F113A468-ADE7-4C20-82C4-33A496456F54",
    type: "Certificate of Incorporation",
  },
  armed_forces_card: { typeId: "5EBD9B1F-BEB2-4BB5-BCB9-35C105F278A5", type: "Armed Forces Card" },
  limitations_benefits: {
    typeId: "5309E91F-6B24-4183-9C7F-3A062658E625",
    type: "Limitations of Benefits Statement",
  },
  election_form: {
    typeId: "DB6649B1-F4BE-41D5-AAAD-4588B253C39D",
    type: "Election form - line 9 option",
  },
  public_register: {
    typeId: "58740ED7-F2EF-481D-B0FA-4703F257B1C6",
    type: "Public Register Extracts",
  },
  driving_license: { typeId: "9FECCC5A-4CC3-46C2-AB7D-53FB2E3DB0B8", type: "Driving License" },
  driverLicense: { typeId: "9FECCC5A-4CC3-46C2-AB7D-53FB2E3DB0B8", type: "Driving License" },
  state_eu: { typeId: "D525BDE1-E0A2-4F2A-B2CE-5AD75383264E", type: "State of the EU" },
  email: { typeId: "C2676B9E-BCFA-497E-8588-6B8B1DA00914", type: "eMail" },
  retroactive_statement: {
    typeId: "3DC45284-ACA4-494B-AA1F-6EBA8736F9E1",
    type: "Retroactive Statement",
  },
  birth_certificate: { typeId: "70612B92-D0F3-4347-9CC3-912B9597C800", type: "Birth Certificate" },
  passport: { typeId: "70825FED-AEBF-4EB8-949D-998D69D718F9", type: "Passport" },
  withholding_statement: {
    typeId: "872ACA95-E43E-4EFA-A494-9A4A97356365",
    type: "Withholding Statement",
  },
  email_alt: { typeId: "325FFDDE-2558-4E32-B7DA-A67435BF59F9", type: "eMail" },
  other: { typeId: "E697B03A-91D1-4CEB-8EC4-A7A4E53E5B30", type: "Other" },
  hybrid_status: {
    typeId: "5A447556-7724-484B-A6D4-BAE392B8FF22",
    type: "Hybrid Status - Supporting Documentation",
  },
  notional_principle: {
    typeId: "BB810C57-8652-4431-BEB2-BE50057C17DC",
    type: "Notional principle contracts statement",
  },
  connected_documentation: {
    typeId: "ACCDB44A-2518-4B06-95C2-BF837E64E343",
    type: "Connected documentation",
  },
  national_id_member: {
    typeId: "4EA07713-50FD-4427-AB0A-C3972E3A7BB0",
    type: "National ID Card of A Member",
  },
  form_w4: { typeId: "D9AFD794-205A-45C3-A64A-C5219F20CBB2", type: "Form W-4" },
  proof_residency: {
    typeId: "F1BC6DC5-19C6-441F-A133-CEDE22778968",
    type: "Proof of Residency Document",
  },
  affidavit: { typeId: "B6477F29-2FE4-4CC0-A58E-CF7B91F791E7", type: "Affidavit" },
  national_id: { typeId: "405AE4A8-A1FA-45EB-9449-D1C76EC101FB", type: "National ID Card" },
  effectively_connected: {
    typeId: "BD2028C8-56A5-4404-895A-D36F42535DDB",
    type: "Effectively connected income statement - line 9 option",
  },
  power_attorney: {
    typeId: "59AC92CD-62C8-46DC-BC33-DBAFA0716242",
    type: "Power of attorney statement",
  },
  us_counsel: {
    typeId: "B6A5ABBA-E61E-4C5C-9B43-E051D75A26F6",
    type: "U.S. counsel opinion statement",
  },
  work_visa: { typeId: "1F7944EA-CF76-40FB-B182-E3D1DE59A74A", type: "Work visa" },
  expatriate: { typeId: "7AE8E536-21FD-46A2-93B7-EE99477D7DE2", type: "Expatriate Documentation" },
};

// Helper function to get document type info
const getDocumentTypeInfo = (documentType: string) => {
  // Normalize the document type by removing common prefixes and converting to lowercase
  const normalizedType = documentType
    .toLowerCase()
    .replace(/^idDocument\./, "")
    .replace(/\.pdf$/, "");

  // Try direct match first
  if (documentTypeMapping[normalizedType]) {
    return documentTypeMapping[normalizedType];
  }

  // Try partial matches for common document types
  if (normalizedType.includes("passport")) {
    return documentTypeMapping["passport"];
  }
  if (normalizedType.includes("driver") || normalizedType.includes("license")) {
    return documentTypeMapping["driving_license"];
  }
  if (normalizedType.includes("national") && normalizedType.includes("id")) {
    return documentTypeMapping["national_id"];
  }

  // Default to 'Other' if no match found
  return documentTypeMapping["other"];
};

export const generateW8BENEXML = async (formData: any) => {
  // Helper function to safely get values
  const getValue = (key: string, defaultValue: string = "") => formData[key] || defaultValue;

  // Helper function to safely get boolean values
  const getBooleanValue = (key: string) => {
    const value = formData[key];
    if (
      value === true ||
      value === "true" ||
      value === "yes" ||
      value === "selected" ||
      value === "1" ||
      value === 1
    ) {
      return "true";
    }
    return "false";
  };

  // Helper function to determine entity type from consolidated field or Chapter 3 Status checkboxes
  const getEntityType = () => {
    // Check more specific types first
    if (formData["W8-BENE-4_foreign_government_controlled_entity"])
      return "Foreign Government - Controlled Entity";
    if (formData["W8-BENE-4_foreign_government_integral_part"])
      return "Foreign Government - Integral Part";
    if (formData["W8-BENE-4_international_organization"]) return "International Organization";
    if (formData["W8-BENE-4_tax_exempt_organization"]) return "Tax-Exempt Organization";
    if (formData["W8-BENE-4_private_foundation"]) return "Private Foundation";
    if (formData["W8-BENE-4_central_bank"]) return "Central Bank of Issue";
    if (formData["W8-BENE-4_disregarded_entity"]) return "Disregarded Entity";
    if (formData["W8-BENE-4_complex_trust"]) return "Complex Trust";
    if (formData["W8-BENE-4_simple_trust"]) return "Simple Trust";
    if (formData["W8-BENE-4_grantor_trust"]) return "Grantor Trust";
    if (formData["W8-BENE-4_estate"]) return "Estate";
    if (formData["W8-BENE-4_partnership"]) return "Partnership";
    if (formData["W8-BENE-4_corporation"]) return "Corporation";

    console.log("⚠️ No Entity Type checkbox found, defaulting to 'Corporation'");
    return ""; // Default
  };
  console.log("selected entity type", getEntityType());
  // Helper function to determine Chapter 4 Status from consolidated field or FATCA Status checkboxes
  const getChapter4Status = () => {
    // Fallback to old individual checkbox fields for backward compatibility
    if (formData["W8-BENE-5_participating_ffi"]) return "Participating FFI";
    if (formData["W8-BENE-5_reporting_model1"]) return "Reporting Model 1 FFI";
    if (formData["W8-BENE-5_reporting_model2"]) return "Reporting Model 2 FFI";
    if (formData["W8-BENE-5_nonparticipating_ffi"]) return "Nonparticipating FFI";
    if (formData["W8-BENE-5_registered_deemed"]) return "Registered deemed-compliant FFI";
    if (formData["W8-BENE-5_sponsored_ffi"]) return "Sponsored FFI";
    if (formData["W8-BENE-5_certified_local_bank"])
      return "Certified deemed-compliant nonregistering local bank";
    if (formData["W8-BENE-5_certified_low_value"])
      return "Certified deemed-compliant FFI with only low-value accounts";
    if (formData["W8-BENE-5_certified_sponsored"])
      return "Certified deemed-compliant sponsored, closely held investment vehicle";
    if (formData["W8-BENE-5_certified_limited_life"])
      return "Certified deemed-compliant limited life debt investment entity";
    if (formData["W8-BENE-5_investment_entities"])
      return "Certain investment entities that do not maintain financial accounts";
    if (formData["W8-BENE-5_owner_documented"]) return "Owner documented FFI";
    if (formData["W8-BENE-5_restricted_distributor"]) return "Restricted distributor";
    if (formData["W8-BENE-5_nonreporting_iga"]) return "Nonreporting IGA FFI";
    if (formData["W8-BENE-5_foreign_government"])
      return "Foreign government, government of a U.S. possession, or foreign central bank of issue";
    if (formData["W8-BENE-5_international_organization"]) return "International organization";
    if (formData["W8-BENE-5_exempt_retirement"]) return "Exempt retirement plans";
    if (formData["W8-BENE-5_entity_exempt_owners"])
      return "Entity wholly owned by exempt beneficial owners";
    if (formData["W8-BENE-5_territory_financial"]) return "Territory financial institution";
    if (formData["W8-BENE-5_excepted_group"]) return "Excepted nonfinancial group entity";
    if (formData["W8-BENE-5_excepted_startup"]) return "Excepted nonfinancial start-up company";
    if (formData["W8-BENE-5_excepted_liquidation"])
      return "Excepted nonfinancial entity in liquidation or bankruptcy";
    if (formData["W8-BENE-5_fatca_501c_org_"]) return "501(c) organization";
    if (formData["W8-BENE-5_nonprofit_org"]) return "Nonprofit organization";
    if (formData["W8-BENE-5_publicly_traded"])
      return "Publicly traded NFFE or NFFE affiliate of a publicly traded corporation";
    if (formData["W8-BENE-5_excepted_territory"]) return "Excepted territory NFFE";
    if (formData["W8-BENE-5_active_nffe"]) return "Active NFFE";
    if (formData["W8-BENE-5_passive_nffe"]) return "Passive NFFE";
    if (formData["W8-BENE-5_excepted_affiliate"]) return "Excepted inter-affiliate FFI";
    if (formData["W8-BENE-5_direct_reporting"]) return "Direct reporting NFE";
    if (formData["W8-BENE-5_sponsored_direct"]) return "Sponsored direct reporting NFFE";
    if (formData["W8-BENE-5_account_not_financial"])
      return "Account that is not a financial account";

    console.log("⚠️ No Chapter 4 status checkbox found, defaulting to 'Active NFFE'");
    return ""; // Default
  };

  console.log("selected chapter4 status", getChapter4Status());
  const currentDate = new Date().toISOString();
  const getDREFATCAEntitytype = () => {
    // Check for specific DRE entity types
    if (formData["W8-BENE-11_branch_treated_as_nonparticipating_FFI"])
      return "Branch treated as nonparticipating FFI";
    if (formData["W8-BENE-11_participating_FFI"]) return "Participating FFI";
    if (formData["W8-BENE-11_reporting_Model1_FFI"]) return "Reporting Model 1 FFI";
    if (formData["W8-BENE-11_reporting_Model2_FFI"]) return "Reporting Model 2 FFI";
    if (formData["W8-BENE-11_us_branch"]) return "U.S. Branch";

    return "";
  };

  // Generate related documents XML
  const generateRelatedDocumentsXml = async () => {
    let relatedDocumentsXml = "";

    // Handle single document case from original code
    if (formData.idDocumentUrl) {
      const docContent = await getPDFBase64(formData.idDocumentUrl);
      const typeId = formData.idDocumentType || "Other";

      relatedDocumentsXml += `
      <RelatedDocument>
        <ID>${uuidv4()}</ID>
        <Type>${typeId}</Type>
        <TypeID>${typeId}</TypeID>
        <File>
          <Name>${formData.idDocumentType || "id_document"}.pdf</Name>
          <Data>${docContent}</Data>
        </File>
      </RelatedDocument>`;
    }

    // Handle multiple documents if provided in docs array
    if (formData.docs && Array.isArray(formData.docs)) {
      for (const doc of formData.docs) {
        if (doc.url) {
          // Make sure to await the PDF base64 conversion
          const docContent = await getPDFBase64(doc.url);
          const typeId = doc.type || "Other";

          relatedDocumentsXml += `
          <RelatedDocument>
            <Type>${doc.type || "Other"}</Type>
            <TypeID>${typeId}</TypeID>
            <File>
              <Name>${doc.name || "document.pdf"}</Name>
              <Data>${docContent}</Data>
            </File>
          </RelatedDocument>`;
        }
      }
    }
    // Handle withholding statement document (attached to first form matching first row of excel)
    if (formData.withholdingStatementUrl) {
      try {
        const whsContent = await getPDFBase64(formData.withholdingStatementUrl);

        relatedDocumentsXml += `
      <RelatedDocument>
        <ID>${uuidv4()}</ID>
        <Type>Withholding Statement</Type>
        <TypeID>872ACA95-E43E-4EFA-A494-9A4A97356365</TypeID>
        <File>
          <Name>Withholding Statement.pdf</Name>
          <Data>${whsContent}</Data>
        </File>
      </RelatedDocument>`;
      } catch (err) {
        console.error("Failed to attach Withholding Statement:", err);
      }
    }
    return relatedDocumentsXml;
  };
  const relatedDocumentsXml = await generateRelatedDocumentsXml();

  // Generate original file XML if viewPdf is provided
  const generateOriginalFileXml = async () => {
    const viewPdfUrl = getValue("viewPdf", "");
    if (viewPdfUrl) {
      try {
        const originalFileContent = await getPDFBase64(viewPdfUrl, formData.fileImportId);
        const fileName = viewPdfUrl.split("/").pop() || "original_document.pdf";

        return `
        <OriginalFile>
          <Name>${fileName}</Name>
          <Data>${originalFileContent}</Data>
        </OriginalFile>`;
      } catch (error) {
        console.error("Error converting original PDF to base64:", error);
        return "<OriginalFile></OriginalFile>";
      }
    }
    return "<OriginalFile></OriginalFile>";
  };

  const originalFileXml = await generateOriginalFileXml();

  // Parse mailing address components if Mailing address city is not empty
  let mailingAddressParsed = { city: "", state: "", postalCode: "" };
  const mailingCityAddress = getValue("Mailing address city", "");
  if (mailingCityAddress && mailingCityAddress.trim() !== "") {
    mailingAddressParsed = parseAddressComponents(mailingCityAddress);
  }

  // Parse permanent city address if available and not already parsed
  let permanentCityAddressField: { city?: string; state?: string; postalCode?: string } = {};
  const permanentCityAddress = getValue("Permanent city address", "");
  if (permanentCityAddress && permanentCityAddress.trim() !== "") {
    permanentCityAddressField = parseAddressComponents(permanentCityAddress);
  }

  let dreAddressFiled = { city: "", state: "", postalCode: "" };
  const dreCityAddress = getValue("W8-BENE-12_disregarded_entity_city_state_postal", "");
  if (dreCityAddress && dreCityAddress.trim() !== "") {
    dreAddressFiled = parseAddressComponents(dreCityAddress);
  }
  // Debug hybrid treaty claim value - check based on isMapDone flag
  const isMapDone = getBooleanValue("isMapDone") === "true";

  let hybridStatus = "Not Applicable";

  if (isMapDone) {
    // When mapped, use consolidated field
    const hybridTreatyClaimValue = getValue("W8-BENE-4_hybrid_treaty_claim");
    hybridStatus =
      hybridTreatyClaimValue === "yes" || hybridTreatyClaimValue === "Yes"
        ? "Hybrid"
        : "Not Applicable";
  } else {
    // When not mapped, use checkbox field
    const hybridTreatyClaimYes = getBooleanValue("W8-BENE-4_hybrid_treaty_claim_yes");
    hybridStatus = hybridTreatyClaimYes === "true" ? "Hybrid" : "Not Applicable";
  }

  const xml = ` <W_8BEN_E_OCT_2021>
        <UniqueIdentifier>${getValue("UniqueIdentifier")}</UniqueIdentifier>
        <UIDreferenceline>False</UIDreferenceline>
        <Instructor_Identifier>${getValue("entityUniqueIdentifier")}</Instructor_Identifier>
        <BusinessUnit>${getValue("BusinessUnit", "")}</BusinessUnit>
        <ElectronicRecipientStatement>True</ElectronicRecipientStatement>
        <ElectronicRecipientStatementDate>${currentDate}</ElectronicRecipientStatementDate>
        <Entity></Entity>
    <CommonInfo>
        <EntityTypes>
                <EntityType>${getEntityType()}</EntityType>
        </EntityTypes>
        
            <EntityClassification />
            <InitialEntityTypeSelection></InitialEntityTypeSelection>
            <FATCAEntityType>${getChapter4Status()}</FATCAEntityType>
        <Individual>
                <FirstName>${getValue("First name")}</FirstName> 
                <LastName>${getValue("Last name")}</LastName>
                <CountryOfBirth>${getValue("Country of birth")}</CountryOfBirth>
                <CountryOfBirthOther />
                <CountryOfCitizenshipForTax />
                <CountryOfCitizenshipForTaxOther />
                <CityTownOfBirth />
                <PlaceofBirth />
                <AdditionalTaxJurisdictions />
        </Individual>
        <Business>
                <Name>${getValue("Name of organization")}</Name>
                <Country>${getValue("Country of incorporation or organization")}</Country>
                <CountryOther />
                <DoingBusinessAsName>${getValue("W8-BENE-Name_of_disregarded_entity")}</DoingBusinessAsName>
            <DREAddress>
                    <Line1>${getValue("W8-BENE-12_disregarded_entity_address")}</Line1>
                    <Line2>${getValue("Address of disregarded entity line 2") || getValue("DRE address line 2", "")}</Line2>
                    <Line3>${getValue("DRE address line 3") || getValue("Disregarded entity address line 3", "")}</Line3>
                    <City_Town>${dreAddressFiled.city || getValue("WW8-BENE-12_disregarded_entity_city_state_postal")}</City_Town>
                    <State_Province>${getStateCode(dreAddressFiled.state)}</State_Province>
                    <Country>${getValue("W8-BENE-12_disregarded_entity_country")}</Country>
                    <CountryOther />
                    <Zip_PostalCode>${getValue(dreAddressFiled.postalCode)}</Zip_PostalCode>
                    <IsCare_of_address>False</IsCare_of_address>
                    <IsP_O_Box>False</IsP_O_Box>
                    <IsRuralRoute>False</IsRuralRoute>
            </DREAddress>
                <DREGIIN>${getValue("W8-BENE-13_giin")}</DREGIIN>
                <DREFATCAEntitytype>${getDREFATCAEntitytype()}</DREFATCAEntitytype>
                <JurisdictionTaxCountry />
                <SponsoringEntityName />
                <SponsoringEntityGIIN />
                <TrusteeName />
                <TrusteeGIIN />
                <AdditionalTaxJurisdictions />
                <IsSpecifiedUSPerson>true</IsSpecifiedUSPerson>
                <SpecifiedPersonUS>NA</SpecifiedPersonUS>
                <IsSpecifiedUKPerson>true</IsSpecifiedUKPerson>
                <SpecifiedPersonUK>NA</SpecifiedPersonUK>
                <IsSpecifiedCRSPerson>true</IsSpecifiedCRSPerson>
                <SpecifiedPersonCRS>NA</SpecifiedPersonCRS>
        </Business>
        <PermanentAddress>
                <Line1>${getValue("Permanent residence address") || getValue("Permanent residence address") || getValue("Permanent_address_line_1")}</Line1>
                <Line2>${getValue("Permanent address line 2") || getValue("Permanent_address_line_2", "")}</Line2>
                <Line3>${getValue("Permanent address line 3") || getValue("Permanent_address_line_3", "")}</Line3>
                <City_Town>${getValue("Permanent address city") || permanentCityAddressField.city}</City_Town>
                <State_Province>${getStateCode(getValue("Permanent address state USCA") || getStateCode(getValue("Permanent address state NonUS")) || getStateCode(permanentCityAddressField.state || ""))}</State_Province>
                <Country>${getValue("Permanent address country")}</Country>
                <CountryOther>${getValue("Permanent address other") || getValue("Permanent_address_other", "")}</CountryOther>
                <Zip_PostalCode>${getValue("Permanent address zip") || permanentCityAddressField.postalCode}</Zip_PostalCode>
                <IsCare_of_address>${getBooleanValue("Permanent address is care of")}</IsCare_of_address>
                <IsP_O_Box>${getBooleanValue("Permanent address is po box")}</IsP_O_Box>
                <IsRuralRoute>${getBooleanValue("Permanent address is rural route")}</IsRuralRoute>
        </PermanentAddress>
        <MailingAddress>
                <Line1>${getValue("Mailing address")}</Line1>
                <Line2>${getValue("Mailing address line 2") || getValue("Mailing_address_line_2", "")}</Line2>
                <Line3>${getValue("Mailing address line 3") || getValue("Mailing_address_line_3", "")}</Line3>
                <City_Town>${getValue("Mailing city address") || mailingAddressParsed.city}</City_Town>
                <State_Province>${getStateCode(getValue("Mailing address state USCA") || getStateCode(getValue("Mailing address state NonUS")) || getStateCode(mailingAddressParsed.state || ""))}</State_Province>
                <Country>${getValue("Mailing address country")}</Country>
                <CountryOther>${getValue("Mailing address other") || getValue("Mailing_address_other", "")}</CountryOther>
                <Zip_PostalCode>${getValue("Mailing address zip") || mailingAddressParsed.postalCode}</Zip_PostalCode>
                <IsCare_of_address>${getBooleanValue("Mailing address is care of")}</IsCare_of_address>
                <IsP_O_Box>${getBooleanValue("Mailing address is po box")}</IsP_O_Box>
                <IsRuralRoute>${getBooleanValue("Mailing address is rural route")}</IsRuralRoute>
        </MailingAddress>
        <ContactInfo>
                <FirstName>${getValue("First name") || getValue("Contact first name", "")}</FirstName>
                <LastName>${getValue("Last name") || getValue("Contact last name", "")}</LastName>
                <Email>${getValue("Taxpayer email") || getValue("Email", "")}</Email>
                <SignatoryEmail />
                <TelephoneCountry />
                <TelephoneNumber />
                <CellNumber />
        </ContactInfo>
        <HybridStatus>${hybridStatus}</HybridStatus>
        <CitizenshipInfo>
                <IsUSPerson />
                <IsUSGreenCardHolder />
                <IsDualCitizenshipHolder />
                <DoesDualCitizenshipIncludeAmerica />
                <IsBornOrganisedInUS />
                <HasRenouncedCitizenship />
                <DateRenouncedCitizenship />
                <AreCountryForTaxAndBirthDifferent />
                <InUSA31DaysCurrentYear />
        </CitizenshipInfo>
            <IsOnBehalfOfSomeoneElse>True</IsOnBehalfOfSomeoneElse>
        <Proxy>
                <FirstName></FirstName>
                <LastName></LastName>
                <Capacity></Capacity>
                <CapacityOther />
                <PrimaryContactNumber />
        </Proxy>
            <NoUSSiDeclarationConfirmed />
            <NoUSSiDeclarationPart2_3Skipped>False</NoUSSiDeclarationPart2_3Skipped>
        <IgnoredRules>
            <IgnoredRule>
                    <Rule>SIG101</Rule>
                    <Comment />
                </IgnoredRule>
                <IgnoredRule>
                    <Rule>ICOR110</Rule>
                    <Comment />
                </IgnoredRule>
                <IgnoredRule>
                    <Rule>IGA104</Rule>
                    <Comment />
                </IgnoredRule>
                <IgnoredRule>
                    <Rule>A113</Rule>
                    <Comment />
                </IgnoredRule>
                <IgnoredRule>
                    <Rule>Treaty119</Rule>
                    <Comment />
            </IgnoredRule>
        </IgnoredRules>
            <FormCountryConflictAdditionalInformation>I agree to upload my Articles of Incorporation. Test</FormCountryConflictAdditionalInformation>
            <FormCountryConflictReason>U.S. Territory</FormCountryConflictReason>
        <FormCountryConflictAdditionalInformationConfirm>False</FormCountryConflictAdditionalInformationConfirm>
            <FormCountryConflictContentBlock />
            <NonUSIndividual_IsClaimingECI />
            <NonUSIndividual_IsForIndependentPersonalServices />
            <NonUSIndividual_IsPublicEntertainer />
            <NonUSIndividual_IsResidentNoSpecificProvision />
            <NonUSIndividual_IsForDependentPersonalServices />
            <NonUSIndividual_IsForScholarshipFellowshipAndPersonalServicesIncome />
            <NonUSIndividual_PartOfWhichNotClaimingTaxTreatyWithholdingExemption />
            <NonUSIndividual_IsNotClaimingTaxTreatyWithholdingExemption />
            <NonUSIndividual_IsForScholarshipFellowshipNoPersonalServicesIncome />
            <NonUSIndividual_IsForOnlyForeignStatusOrTreatyBenefits />
            <SubmissionGroupId>ef00b358-7ec6-4307-a732-869d112a39bb</SubmissionGroupId>
            <SelectedOptionsonGuide4 />
          
    </CommonInfo>
    <TaxpayerIdentification>
            <Type>${getValue("Taxpayer type", "")}</Type>
            <EIN>${getValue("U.S. taxpayer identification number", "")}</EIN>
            <GIIN>${getValue("GIIN")}</GIIN>
            <GIINAppliedFor>${getBooleanValue("GIIN Applied For")}</GIINAppliedFor>
            <GIINNotApplicable>False</GIINNotApplicable>
            <ForeignTaxpayerId>${getValue("W8-BENE-Foreign_tax_identifying_number")}</ForeignTaxpayerId>
            <TINFormatNotAvail>False</TINFormatNotAvail>
        <ForeignTaxpayerCountry></ForeignTaxpayerCountry>
            <ForeignTinUnavailable>False</ForeignTinUnavailable>
            <ForeignTinUnavailableReason />
            <IsAppliedForTIN>False</IsAppliedForTIN>
            <UnexpectedTINTypeExplanation />
            <UnexpectedTINTypeExplanationConfirmed />
            <UnexpectedTINTypeContentBlock />
            <CheckifFTINnotlegallyrequired>${getBooleanValue("W8-BENE-FTIN_not_legally_required")}</CheckifFTINnotlegallyrequired>
            <IsFurtherExplanation>False</IsFurtherExplanation>
            <WrittenStatementReasons />
            <WrittenStatementReasonsAdditionalInformation />
        <TINCheck>
                <IsCheckPreformed>False</IsCheckPreformed>
                <IsSuccessful>False</IsSuccessful>
                <Date></Date>
        </TINCheck>
    </TaxpayerIdentification>
    <SignInfo>
            <IsSigned>${getBooleanValue("isMapDone") === "false" ? (getValue("Signature", "").trim() !== "" ? true : false) : getValue("isSigned") === "yes" ? true : false}</IsSigned>
            <By>${getValue("Signer_printed_name")}</By>
            <Capacity>${getValue("Signing_capacity", "")}</Capacity>
            <SignatureUnreadable>${getBooleanValue("Signature_unreadable")}</SignatureUnreadable>
            <Date>${normalizeDateToSlash(getValue("Signature_date"))}</Date>
            <DateOnForm>${getValue("Signature_date_on_form") || getValue("Signature_date")}</DateOnForm>
            <UserLocalTime>${new Date().toLocaleString()}</UserLocalTime>
            <UserLocalTimeZone>India Standard Time</UserLocalTimeZone>
            <EnteredConfirmationCode>L5MY5W</EnteredConfirmationCode>
    </SignInfo>
              <Tag>
              
                <CreatedByApp>Extractor</CreatedByApp>
              </Tag>
     
    <RelatedDocuments>
           ${relatedDocumentsXml}
    </RelatedDocuments>
    ${originalFileXml}
        <ReferenceNumbers>${getValue("W8-BENE-10_reference_number")}</ReferenceNumbers>
        <PartIIILine14a>${getBooleanValue("W8-BENE-14A_beneficial_owner_resident")}</PartIIILine14a>
        <PartIIILine14b>${getBooleanValue("W8-BENE-14B_beneficial_owner_derives_item")}</PartIIILine14b>
        <PartIIILine14c>${getBooleanValue("W8-BENE-14C_claiming_treaty_benefits")}</PartIIILine14c>
        <ClaimedArticle>${getValue("W8-BENE-14B_other_text")}</ClaimedArticle>
        <ClaimedCountry>${getValue("W8-BENE-14A_country_name")}</ClaimedCountry>
    <ClaimedCountryOther></ClaimedCountryOther>
    <ClaimedExplanation>${getValue("W8-BENE-15_additional_conditions_explaination")}</ClaimedExplanation>
    <SRCs>
      <SRC>
        <ClaimedArticle>${getValue("W8-BENE-15-Treaty_article_0", "") || getValue("W8-BENE-15_treaty_provision_and_paragraph")}</ClaimedArticle>
        <ClaimedPercentage>${getValue("W8-BENE-15-Treaty_percentage_0", "") || getValue("W8-BENE-15_withholding_rate_percentage")}</ClaimedPercentage>
        <ClaimedTypeOfIncome>${getValue("W8-BENE-15-Treaty_typeOfIncome_0", "") || getValue("W8-BENE-15_income_type")}</ClaimedTypeOfIncome>
        <Status></Status>
        <ClaimedParagraph>${getValue("W8-BENE-15-Treaty_paragraph_0", "")}</ClaimedParagraph>
        <ClaimedSubparagraph>${getValue("W8-BENE-15-Treaty_subparagraph_0", "")}</ClaimedSubparagraph>
      </SRC>
    </SRCs>  
   
        <PartIVLine16NameOfSponsoringEntity>${getValue("W8-BENE-16_name_of_sponsoring_entity")}</PartIVLine16NameOfSponsoringEntity>
        <PartIVLine17Certifya>${getBooleanValue("W8-BENE-17_controlled_foreign_corporation")}</PartIVLine17Certifya>
        <PartIVLine17Certifyb>${getBooleanValue("W8-BENE-17_investment_entity")}</PartIVLine17Certifyb>
        <PartVLine18Certify>${getBooleanValue("W8-BENE-18_licensed_solely")}</PartVLine18Certify>
        <PartVILine19Certify>${getBooleanValue("W8-BENE-19_investing_reinvesting_or_trading")}</PartVILine19Certify>
        <PartVIILine20NameOfSponsoringEntity>${getValue("W8-BENE-20_sponsoring_entity_name")}</PartVIILine20NameOfSponsoringEntity>
        <PartVIILine21Certify>${getBooleanValue("W8-BENE-21_qi_wp_wt")}</PartVIILine21Certify>
        <PartVIIILine22Certify>${getBooleanValue("W8-BENE-22_january_17_2013")}</PartVIIILine22Certify>
        <PartIXLine23Certify>${getBooleanValue("W8-BENE-23_section_1_1471")}</PartIXLine23Certify>
        <PartXLine24aCertify>${getBooleanValue("W8-BENE-24A_owner_documented_FFI")}</PartXLine24aCertify>
        <PartXLine24bCertify>${getBooleanValue("W8-BENE-24B_FFI_owner_reporting")}</PartXLine24bCertify>
        <PartXLine24cCertify>${getBooleanValue("W8-BENE-24C_auditor_letter")}</PartXLine24cCertify>
        <PartXLine24dCertify>${getBooleanValue("W8-BENE-24D_contingent_beneficiaries")}</PartXLine24dCertify>
        <PartXILine25aCertify>${getBooleanValue("W8-BENE-25A_restricted_distributors")}</PartXILine25aCertify>
        <PartXILine25bCertify>${getBooleanValue("W8-BENE-25B_restricted_distributor_bound_agreement")}</PartXILine25bCertify>
        <PartXILine25cCertify>${getBooleanValue("W8-BENE-25C_restricted_distributor_current_agreement")}</PartXILine25cCertify>
        <PartXIILine26Certify>${getBooleanValue("W8-BENE-26_nonreporting_iga_country")}</PartXIILine26Certify>
    <PartXIIIGACountry>${getValue("W8-BENE-26_country_name")}</PartXIIIGACountry>
    <PartXIIIGACountryOther>${getValue("W8-BENE-26_country_other")}</PartXIIIGACountryOther>
        <PartXIITreatedAs>${getBooleanValue("W8-BENE-26_model1_iga")}</PartXIITreatedAs>
        <PartXII_TrusteeUS>${getBooleanValue("W8-BENE-26_trustee_us")}</PartXII_TrusteeUS>
        <PartXII_TrusteeForeign>${getBooleanValue("W8-BENE-26_trustee_foreign")}</PartXII_TrusteeForeign>
    <PartXIITreatedAsValue>${getValue("W8-BENE-26_treated_as")}</PartXIITreatedAsValue>
        <PartXIIILine27Certify>${getBooleanValue("W8-BENE-27_foreign_government_checkbox")}</PartXIIILine27Certify>
        <PartXIVLine28aCertify>${getBooleanValue("W8-BENE-28A_international_org_7701_checkbox")}</PartXIVLine28aCertify>
        <PartXIVLine28bCertify>${getBooleanValue("W8-BENE-28B_international_org_intergovernmental")}</PartXIVLine28bCertify>
        <PartXVLine29aCertify>${getBooleanValue("W8-BENE-29A_retirement_plan_treaty")}</PartXVLine29aCertify>
        <PartXVLine29bCertify>${getBooleanValue("W8-BENE-29B_retirement_plan_organized")}</PartXVLine29bCertify>
        <PartXVLine29cCertify>${getBooleanValue("W8-BENE-29C_retirement_plan_fewer_50")}</PartXVLine29cCertify>
        <PartXVLine29dCertify>${getBooleanValue("W8-BENE-29D_retirement_plan_401a")}</PartXVLine29dCertify>
        <PartXVLine29eCertify>${getBooleanValue("W8-BENE-29E_retirement_plan_earn_income")}</PartXVLine29eCertify>
        <PartXVLine29fCertify>${getBooleanValue("W8-BENE-29F_retirement_plan_government_sponsored")}</PartXVLine29fCertify>
        <PartXVILine30Certify>${getBooleanValue("W8-BENE-30_entity_wholly_owned")}</PartXVILine30Certify>
        <PartXVIILine31Certify>${getBooleanValue("W8-BENE-31_territory_financial_institution")}</PartXVIILine31Certify>
        <PartXVIIILine32Certify>${getBooleanValue("W8-BENE-32_excepted_nonfinancial_group")}</PartXVIIILine32Certify>
        <PartXIXLine33Certify>${getBooleanValue("W8-BENE-33_excepted_startup")}</PartXIXLine33Certify>
        <PartXIXLine33Date>${getValue("W8-BENE-33_excepted_startup_formation_date")}</PartXIXLine33Date>
        <PartXXLine34Certify>${getBooleanValue("W8-BENE-34_excepted_liquidation")}</PartXXLine34Certify>
        <PartXXLine34Date>${getValue("W8-BENE-34_excepted_liquidation_filing_date")}</PartXXLine34Date>
        <PartXXILine35Certify>${getBooleanValue("W8-BENE_35_section_501c")}</PartXXILine35Certify>
        <PartXXILine35LetterDate>${getValue("W8-BENE-35_section_501c_determination_date")}</PartXXILine35LetterDate>
        <PartXXIILine36Certify>${getBooleanValue("W8-BENE-36_nonprofit_organization")}</PartXXIILine36Certify>
        <PartXXIIILine37aCertify>${getBooleanValue("W8-BENE-37A_publicly_traded_nffe")}</PartXXIIILine37aCertify>
        <PartXXIIILine37aSecuritiesExchange>${getValue("W8-BENE-37A_securities_exchange_name")}</PartXXIIILine37aSecuritiesExchange>
        <PartXXIIILine37bCertify>${getBooleanValue("W8-BENE-37B_nffe_affiliate")}</PartXXIIILine37bCertify>
        <PartXXIIILine37bEntityName>${getValue("W8-BENE-37B_entity_name")}</PartXXIIILine37bEntityName>
        <PartXXIIILine37bSecuritiesMarketName>${getValue("W8-BENE-37B_name_of_securities")}</PartXXIIILine37bSecuritiesMarketName>
        <PartXXIVLine38Certify>${getBooleanValue("W8-BENE-38_excepted_territory_nffe")}</PartXXIVLine38Certify>
        <PartXXVLine39Certify>${getBooleanValue("W8-BENE-39_active_nffe")}</PartXXVLine39Certify>
        <PartXXVILine40aCertify>${getBooleanValue("W8-BENE-40A_traded_nffe")}</PartXXVILine40aCertify>
        <PartXXVILine40bCertify>${getBooleanValue("W8-BENE-40B_passive_nffe_no_substantial_owners")}</PartXXVILine40bCertify>
        <PartXXVILine40cCertify>${getBooleanValue("W8-BENE-40C_passive_nffe_substantial_owners_provided")}</PartXXVILine40cCertify>
        <PartXXVIILine41Certify>${getBooleanValue("W8-BENE-41_affiliated_group")}</PartXXVIILine41Certify>
        <PartXXVIIILine42SponsoringEntity>${getValue("W8-BENE-42_sponsoring_entity")}</PartXXVIIILine42SponsoringEntity>
        <PartXXVIIILine43Certify>${getBooleanValue("W8-BENE-43_nffe")}</PartXXVIIILine43Certify>
    <PartIVLine16GIINOfSponsoringEntity></PartIVLine16GIINOfSponsoringEntity>
        <PartXIILine26Model1IGA>${getBooleanValue("W8-BENE-26_model1_iga")}</PartXIILine26Model1IGA>
        <PartXIILine26Model2IGA>${getBooleanValue("W8-BENE-26_model2_iga")}</PartXIILine26Model2IGA>
    <PartXII_IGAGIIN></PartXII_IGAGIIN>
  <PartIIILine14Government>${getBooleanValue("W8-BENE-14B_government")}</PartIIILine14Government>
  <PartIIILine14cmpownership>${getBooleanValue("W8-BENE-14B_company_meets_ownership_and_base_erosion_test")}</PartIIILine14cmpownership>
  <PartIIILine14Taxememptpension>${getBooleanValue("W8-BENE-14B_tax_exempt_pension_trust")}</PartIIILine14Taxememptpension>
  <PartIIILine14cmpderivative>${getBooleanValue("W8-BENE-14B_company_meets_derivative")}</PartIIILine14cmpderivative>
  <PartIIILine14Othertaxexemptorg>${getBooleanValue("W8-BENE-14B_other_tax_exempt_organization")}</PartIIILine14Othertaxexemptorg>
  <PartIIILine14cmpactivetrade>${getBooleanValue("W8-BENE-14B_company_meets_active_trade_or_business")}</PartIIILine14cmpactivetrade>
  <PartIIILine14publicitytradedcorp>${getBooleanValue("W8-BENE-14B_publicly_traded_corporation")}</PartIIILine14publicitytradedcorp>
  <PartIIILine14favdis>${getBooleanValue("W8-BENE-14B_favorable_discretionary_determination")}</PartIIILine14favdis>
  <PartIIILine14subsidiarypublicalytradedcorp>${getBooleanValue("W8-BENE-14B_subsidiary_of_publicly_traded")}</PartIIILine14subsidiarypublicalytradedcorp>
  <PartIIILine14Other>${getBooleanValue("W8-BENE-14B_other")}</PartIIILine14Other>
	<PartIIINoLOBarticleInTreaty>${getBooleanValue("W8-BENE-14B_no_lob")}</PartIIINoLOBarticleInTreaty>
	<PartIIIOtherRemark>${getValue("W8-BENE-14B_other_text")}</PartIIIOtherRemark>
  <SignatureCertify>${getBooleanValue("Signing_capacity")}</SignatureCertify>
  <PartXXVIIILine42GIINSponsoringEntity></PartXXVIIILine42GIINSponsoringEntity>
        <PartXII_NameofTrusteeorsponsor>${getValue("W8-BENE-26_trustee_sponsor_name")}</PartXII_NameofTrusteeorsponsor>
   
        <SignatureCertify></SignatureCertify>
        <SubstituteFormStatement>The Internal Revenue Service does not require your consent to any provisions of this document other than the certifications required to establish your status as a non-U.S. person and, if applicable, obtain a reduced rate of withholding.</SubstituteFormStatement>
       <SubstantialUSOwners>
${[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  .map((num) => {
    const name = getValue(`W8-BENE-substantial_owner_${num}_name`);
    const address = getValue(`W8-BENE-substantial_owner_${num}_address`);
    const tin = getValue(`W8-BENE-substantial_owner_${num}_tin`);

    // Only include owner if at least name is provided
    if (name && name.trim() !== "") {
      return `
<SubstantialUSOwner>
<Name>${name}</Name>
<Address>
<Line1>${address}</Line1>
<Line2/>
<Line3/>
<City_Town/>
<State_Province/>
<Country/>
<CountryOther/>
<Zip_PostalCode/>
</Address>
<TIN>${tin}</TIN>
</SubstantialUSOwner>`;
    }
    return "";
  })
  .join("")}
</SubstantialUSOwners>
</W_8BEN_E_OCT_2021>`;
  // console.log("Generated W8BEN-E XML:", xml);

  return escapeXmlText(xml);
};
